import type { Newsroom, NewsroomCompanyInformation, Story } from '@prezly/sdk';
import { LocaleObject } from '@prezly/theme-kit-core';

import { getSocialLinks } from '@/components/SocialMedia/utils';

export type JsonLdSchema = Record<string, unknown>;

type PublisherNewsroom = Pick<Newsroom, 'name' | 'url'>;
type PublisherCompanyInformation = NewsroomCompanyInformation;

export function serializeJsonLd(schema: JsonLdSchema | JsonLdSchema[]): string {
    return JSON.stringify(schema).replace(/</g, '\\u003c');
}

function buildPublisherSchema({
    newsroom,
    companyInformation,
}: {
    newsroom: PublisherNewsroom;
    companyInformation: PublisherCompanyInformation;
}): JsonLdSchema {
    const sameAs = Object.values(getSocialLinks(companyInformation)).filter((url): url is string =>
        Boolean(url),
    );

    return {
        '@type': 'Organization',
        name: companyInformation.name || newsroom.name,
        url: newsroom.url,
        ...(sameAs.length > 0 && { sameAs }),
    };
}

export function buildOrganizationSchema({
    newsroom,
    companyInformation,
}: {
    newsroom: PublisherNewsroom;
    companyInformation: PublisherCompanyInformation;
}): JsonLdSchema {
    const { seo_settings, about_plaintext } = companyInformation;
    const description =
        seo_settings.meta_description || seo_settings.default_meta_description || about_plaintext;

    return {
        '@context': 'https://schema.org',
        ...buildPublisherSchema({ newsroom, companyInformation }),
        ...(description && { description }),
    };
}

export function buildWebsiteSchema({
    newsroom,
    companyInformation,
}: {
    newsroom: PublisherNewsroom;
    companyInformation: PublisherCompanyInformation;
}): JsonLdSchema {
    const name = companyInformation.name || newsroom.name;
    let hostname: string | undefined;

    try {
        hostname = new URL(newsroom.url).hostname.toLowerCase();
    } catch {
        hostname = undefined;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name,
        url: newsroom.url,
        ...(hostname && hostname !== name.toLowerCase() && { alternateName: hostname }),
    };
}

export function buildNewsArticleSchema({
    story,
    newsroom,
    companyInformation,
}: {
    story: Story;
    newsroom: PublisherNewsroom;
    companyInformation: PublisherCompanyInformation;
}): JsonLdSchema {
    const headline =
        story.seo_settings.meta_title || story.seo_settings.default_meta_title || story.title;
    const description =
        story.seo_settings.meta_description ||
        story.seo_settings.default_meta_description ||
        story.subtitle ||
        story.summary;
    const url = story.seo_settings.canonical_url || story.oembed.url;
    const thumbnail = story.oembed.thumbnail_url;

    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline,
        ...(description && { description }),
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        ...(thumbnail && {
            image: {
                '@type': 'ImageObject',
                url: thumbnail,
                ...(story.oembed.thumbnail_width && { width: story.oembed.thumbnail_width }),
                ...(story.oembed.thumbnail_height && { height: story.oembed.thumbnail_height }),
            },
        }),
        ...(story.published_at && { datePublished: story.published_at }),
        dateModified: story.updated_at,
        ...(story.author?.display_name && {
            author: { '@type': 'Person', name: story.author.display_name },
        }),
        publisher: buildPublisherSchema({ newsroom, companyInformation }),
        inLanguage: LocaleObject.fromAnyCode(story.culture.code).toHyphenCode(),
    };
}
